import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ZfBanner, ZfIcon, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import { CountryRiskWaiversGrid, WaiverRow } from './CountryRiskWaiversGrid';

const sectionStyle = {
  border: '1px solid var(--border-color-default-subtle, #e0e2e8)',
  padding: 'var(--padding-m, 24px)',
};

const descriptionStyle = { maxWidth: '60%' };

type SectionProps = {
  title: string;
  description: ReactNode;
  children: ReactNode;
};

const Section = ({ title, description, children }: SectionProps) => (
  <ZfStack direction="column" spacing="medium" style={sectionStyle}>
    <ZfStack direction="column" spacing="extra-small">
      <ZfTypography variant="title-medium" color="emphasis">
        {title}
      </ZfTypography>
      <ZfTypography variant="body-medium" color="muted" style={descriptionStyle}>
        {description}
      </ZfTypography>
    </ZfStack>
    {children}
  </ZfStack>
);

export const CountryRiskWaivers = () => {
  const { t } = useTranslation();
  const [globalWaiverExpanded, setGlobalWaiverExpanded] = useState(false);

  const globalWaiverRows: WaiverRow[] = [
    {
      id: 'global',
      waiver: t('pb.countryriskwaivers.global.title'),
      status: t('pb.countryriskwaivers.status.active'),
    },
  ];

  const globalWaiverDescription = globalWaiverExpanded
    ? t('pb.countryriskwaivers.global.description.full')
    : t('pb.countryriskwaivers.global.description.short');

  return (
    <ZfStack
      direction="column"
      spacing="medium"
      data-testid="country-risk-waivers"
      style={{ padding: 'var(--padding-m, 24px)' }}
    >
      <ZfStack direction="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <ZfTypography variant="heading-small" color="emphasis">
          {t('pb.countryriskwaivers.title')}
        </ZfTypography>
        <ZfTextButton size="medium" hierarchy="tertiary">
          <ZfIcon slot="trailing" name="open_in_new" size="medium" />
          {t('pb.overview.open.documents.crm.label')}
        </ZfTextButton>
      </ZfStack>

      <ZfBanner type="info" dismissible={false} description={t('pb.countryriskwaivers.banner')} />

      <Section
        title={t('pb.countryriskwaivers.global.title')}
        description={
          <>
            {globalWaiverDescription}{' '}
            <ZfTextButton size="small" hierarchy="tertiary" onClick={() => setGlobalWaiverExpanded((v) => !v)}>
              {globalWaiverExpanded
                ? t('pb.countryriskwaivers.global.viewless')
                : t('pb.countryriskwaivers.global.viewmore')}
            </ZfTextButton>
          </>
        }
      >
        <CountryRiskWaiversGrid rows={globalWaiverRows} testId="global-waiver-grid" />
      </Section>

      <Section
        title={t('pb.countryriskwaivers.marketspecific.title')}
        description={t('pb.countryriskwaivers.marketspecific.description')}
      >
        <CountryRiskWaiversGrid rows={[]} testId="market-specific-waiver-grid" />
      </Section>

      <Section
        title={t('pb.countryriskwaivers.subaccounts.title')}
        description={t('pb.countryriskwaivers.subaccounts.description')}
      >
        <ZfBanner
          type="info"
          dismissible={false}
          titleText={t('pb.countryriskwaivers.subaccounts.frontusers.title')}
          description={t('pb.countryriskwaivers.subaccounts.frontusers.description')}
        >
          <ZfStack slot="trailing" direction="row" spacing="small" style={{ alignItems: 'center' }}>
            <ZfTextButton size="small" hierarchy="tertiary">
              <ZfIcon slot="trailing" name="mail" size="medium" />
              {t('pb.countryriskwaivers.subaccounts.contactgnm')}
            </ZfTextButton>
            <ZfTextButton size="small" hierarchy="secondary">
              {t('pb.countryriskwaivers.subaccounts.custodyregulations')}
            </ZfTextButton>
          </ZfStack>
        </ZfBanner>
        <CountryRiskWaiversGrid rows={[]} testId="subaccount-waiver-grid" />
      </Section>
    </ZfStack>
  );
};
